import * as React from "react";
import { createPortal } from "react-dom";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  Stack,
  Tooltip,
  IconButton,
  Button,
  Chip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import NoRows from "../../../components/DataGrid/animations/NoRows";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import httpClient from "../../../utils/httpClient";
import { useNavigate } from "react-router-dom";
import moment, { Moment } from "moment";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import {
  applyFilters,
  buildFilterSummary,
  SOLD_PRINT_PORTAL_ID,
  PrintColumn,
  FilterItem,
  Filters,
} from "../../../utils/listExport";
import {
  getSoldFilters,
  setSoldFilters,
  getSoldPagination,
  setSoldPagination,
  ListFilters,
} from "../../../utils/localStorage";
import { ListPageToolbar } from "../../../components/ListPageToolbar/ListPageToolbar";

// ── Types ─────────────────────────────────────────────────────────────────────

type RowType = {
  analysisHistoryCount: number;
  buyer: string;
  buyerCombined: string;
  jobAddress: string;
  jobId: number;
  jobNumber: string;
  planDescription: string;
  planElevationDescription: string;
  planElevationNumber: string;
  planNumber: string;
  profitAnalysisId: number;
  jobType: string;
  projectId: number;
  projectNumber: string;
  soldDate: string;
  startDate: string;
  subdivision: string;
  stageCode: string;
  stageDate: string;
  profitMargin: number;
  role: string;
  closeDate?: string;
  projectedCloseDate?: string;
  profitAnalysisCreatedOn?: Moment | string | null;
  paModifiedOn?: Moment | string | null;
};

// ── Legend definition ─────────────────────────────────────────────────────────

const LEGEND_ITEMS = [
  { abbr: "Address", full: "Create / Revise / View profit analysis | Job street address" },
  { abbr: "Subdivision", full: "Community / subdivision name" },
  { abbr: "Job #", full: "Project-Job number" },
  { abbr: "Buyer", full: "Combined buyer name(s)" },
  { abbr: "Stage [# days]", full: "Current construction stage code" },
  { abbr: "Profit Proj. %", full: "Profit projection percentage" },
  { abbr: "PA Updated On", full: "Most recent date of profit analysis creation or modification" },
  { abbr: "Proj. Closed Date", full: "Projected close date" },
  { abbr: "Closed Date", full: "Actual close date" },
];

// ── Default state ─────────────────────────────────────────────────────────────

const DEFAULT_STAGE = "All";
const DEFAULT_SEARCH = "";
const DEFAULT_COMMUNITIES: string[] = [];
const DEFAULT_PAGINATION = { page: 0, pageSize: 10000 };
const EMPTY_FILTERS: Filters = { items: [], quickFilterValues: [] };

// ── Helpers ───────────────────────────────────────────────────────────────────

const toCleanString = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s.length ? s : null;
};

const sanitizeFilterItems = (items: FilterItem[]): FilterItem[] => {
  const result: FilterItem[] = [];

  for (const item of items ?? []) {
    if (!item) continue;
    const raw = item.value as unknown;

    if (Array.isArray(raw)) {
      const cleaned = raw.map(toCleanString).filter((v): v is string => Boolean(v));
      if (cleaned.length === 0) continue;
      result.push({ ...item, operator: "isAnyOf", value: cleaned });
    } else {
      const cleaned = toCleanString(raw);
      if (!cleaned) continue;
      result.push({ ...item, value: cleaned });
    }
  }

  return result;
};

const sanitizeQuickFilter = (values: unknown[]): string[] =>
  (values ?? []).map(toCleanString).filter((v): v is string => Boolean(v));

const buildDataGridFilter = (listFilters: ListFilters): Filters => ({
  items: sanitizeFilterItems(listFilters.items ?? []),
  quickFilterValues: sanitizeQuickFilter(
    (listFilters.quickFilterValues ?? []) as unknown[]
  ),
});

const rebuildItems = (
  currentItems: FilterItem[],
  field: string,
  value: string | string[] | "All" | null
): FilterItem[] => {
  const otherItems = (currentItems ?? []).filter((item) => item.field !== field);

  if (value === "All" || value === null || value === undefined) return otherItems;

  if (Array.isArray(value)) {
    const cleaned = value.map(toCleanString).filter((v): v is string => Boolean(v));
    if (cleaned.length === 0) return otherItems;
    return [
      ...otherItems,
      { id: `${field}-multi`, field, operator: "isAnyOf", value: cleaned },
    ];
  }

  const cleaned = toCleanString(value);
  if (!cleaned) return otherItems;
  return [
    ...otherItems,
    { id: `${field}-${cleaned}`, field, operator: "equals", value: cleaned },
  ];
};

// ── Print columns ─────────────────────────────────────────────────────────────

const PRINT_COLUMNS: PrintColumn<RowType>[] = [
  { field: "jobAddress", label: "Address", getValue: (r) => r.jobAddress ?? "" },
  { field: "subdivision", label: "Subdivision", getValue: (r) => r.subdivision ?? "" },
  {
    field: "jobNumber", label: "Job #",
    getValue: (r) => (r.projectNumber && r.jobNumber ? `${r.projectNumber}-${r.jobNumber}` : ""),
  },
  { field: "buyerCombined", label: "Buyer", getValue: (r) => r.buyerCombined ?? "" },
  { field: "stageCode", label: "Stage [# days]", getValue: (r) => r.stageCode + (r.stageDate && r.stageCode === "10" ? ` [${Math.floor((Date.now() - new Date(r.stageDate).getTime()) / 86_400_000)}d]` : "") },
  {
    field: "profitMargin", label: "Profit Proj. %",
    getValue: (r) => (r.profitMargin ? `${r.profitMargin}%` : ""),
  },
  {
    field: "paModifiedOn", label: "PA Updated On",
    getValue: (r) => {
      const v = r.paModifiedOn && r.paModifiedOn !== "0001-01-01T00:00:00"
        ? r.paModifiedOn
        : r.profitAnalysisCreatedOn;
      if (!v || v === "0001-01-01T00:00:00" || r.profitAnalysisId === 0) return "";
      return moment(v).format("L");
    },
  },
  {
    field: "projectedCloseDate", label: "Proj. Closed Date",
    getValue: (r) => (r.projectedCloseDate && r.profitAnalysisId !== 0 ? moment(r.projectedCloseDate).format("L") : ""),
  },
  {
    field: "closeDate", label: "Closed Date",
    getValue: (r) => {
      if (!r.closeDate || r.closeDate === "0001-01-01T00:00:00") return "";
      return moment(r.closeDate).format("L");
    },
  },
];



// ── Mobile card ───────────────────────────────────────────────────────────────

function SoldMobileCard({ row, onNavigate }: { row: RowType; onNavigate: (path: string, state?: any) => void }) {
  const isCreateCase =
    row.profitAnalysisId === 0 && row.role === "Write" && (row.closeDate ?? null) === null;
  const isViewCase =
    (row.profitAnalysisId !== 0 && row.role === "Read") ||
    (row.profitAnalysisId !== 0 && row.role === "Write" &&
      (row.closeDate ?? null) !== null &&
      new Date(row.closeDate!) > new Date(new Date().setDate(new Date().getDate() - 60)));
  const isReviseCase =
    row.profitAnalysisId !== 0 && (row.closeDate ?? null) === null && row.role === "Write";

  const jobNum = row.projectNumber && row.jobNumber
    ? `${row.projectNumber}-${row.jobNumber}` : null;

  const stageDays =
    row.stageDate && row.stageCode === "10"
      ? Math.floor((Date.now() - new Date(row.stageDate).getTime()) / 86_400_000)
      : null;

  const paDate = (() => {
    const v = row.paModifiedOn && row.paModifiedOn !== "0001-01-01T00:00:00"
      ? row.paModifiedOn : row.profitAnalysisCreatedOn;
    if (!v || v === "0001-01-01T00:00:00" || row.profitAnalysisId === 0) return null;
    return moment(v).format("L");
  })();

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        borderRadius: 2,
        border: "1px solid #e5e7eb",
        "&:hover": { borderColor: "#9ca3af", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
      }}
    >
      <CardContent sx={{ pb: "12px !important", pt: 1.5, px: 2 }}>
        {/* Address + action buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827", lineHeight: 1.4, pr: 1 }}>
            {row.jobAddress}
          </Typography>
          <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
            {isViewCase && (
              <Tooltip title="View PA">
                <IconButton size="small" href={`/sold/view/${row.profitAnalysisId}`}>
                  <VisibilityIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {isReviseCase && (
              <Tooltip title="Revise PA">
                <IconButton size="small" href={`/sold/edit/${row.profitAnalysisId}`}>
                  <EditIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {isCreateCase && (
              <Tooltip title="Create PA">
                <IconButton size="small" color="primary" onClick={() => onNavigate("/sold/create", { ...row, method: "fromHist" })}>
                  <AddCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        {/* Subdivision · Job # */}
        <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mb: 0.5 }}>
          {row.subdivision}{jobNum ? ` · ${jobNum}` : ""}
        </Typography>

        {/* Buyer */}
        {row.buyerCombined && (
          <Typography variant="caption" sx={{ color: "#374151", display: "block", mb: 0.75, fontStyle: "italic" }}>
            {row.buyerCombined}
          </Typography>
        )}

        {/* Stage chip + Profit chip */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 0.75 }}>
          {row.stageCode && (
            <Chip
              label={`Stage ${row.stageCode}${stageDays !== null ? ` [${stageDays}d]` : ""}`}
              size="small"
              sx={{ fontSize: "0.7rem", height: 20, backgroundColor: "#f3f4f6", color: "#374151" }}
            />
          )}
          {row.profitMargin ? (
            <Chip
              label={`Profit ${row.profitMargin}%`}
              size="small"
              sx={{ fontSize: "0.7rem", height: 20, backgroundColor: "#ecfdf5", color: "#065f46" }}
            />
          ) : null}
        </Box>

        {/* Dates row */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {row.projectedCloseDate && row.profitAnalysisId !== 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Proj. Close</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827", fontSize: "0.8rem" }}>{moment(row.projectedCloseDate).format("L")}</Typography>
            </Box>
          )}
          {row.closeDate && (
            <Box>
              <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.5 }}>Closed</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827", fontSize: "0.8rem" }}>{moment(row.closeDate).format("L")}</Typography>
            </Box>
          )}
          {paDate && (
            <Box>
              <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 0.5 }}>PA Updated</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827", fontSize: "0.8rem" }}>{paDate}</Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function SoldListPage() {
  const navigation = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ── Initialise state from localStorage ───────────────────────────────────
  const savedFilters = getSoldFilters();
  const savedPagination = getSoldPagination();

  const [rows, setRows] = React.useState<RowType[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  const [filt, setFilt] = React.useState<Filters>(() =>
    buildDataGridFilter(savedFilters)
  );

  const [stage, setStage] = React.useState<string>(
    savedFilters.stage ?? DEFAULT_STAGE
  );
  const [searchValue, setSearchValue] = React.useState<string>(
    (savedFilters.quickFilterValues?.[0] ?? DEFAULT_SEARCH) as string
  );
  const [selectedCommunities, setSelectedCommunities] = React.useState<string[]>(() => {
    const communityItem = savedFilters.items.find((i) => i.field === "subdivision");
    return Array.isArray(communityItem?.value) ? (communityItem!.value as string[]) : [];
  });
  const [communitySearch, setCommunitySearch] = React.useState<string>("");
  const [paginationModel, setPaginationModel] = React.useState(
    savedPagination ?? DEFAULT_PAGINATION
  );

  // ── Derived subdivision list ──────────────────────────────────────────────

  const subdivisions = React.useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.subdivision).filter(Boolean))).sort(),
    [rows]
  );

  const filteredSubdivisions = React.useMemo(() => {
    if (!communitySearch.trim()) return subdivisions;
    const lower = communitySearch.toLowerCase();
    return subdivisions.filter((s) => s.toLowerCase().includes(lower));
  }, [subdivisions, communitySearch]);

  // ── Active filters flag ───────────────────────────────────────────────────

  const hasActiveFilters = React.useMemo(
    () =>
      stage !== DEFAULT_STAGE ||
      searchValue !== DEFAULT_SEARCH ||
      selectedCommunities.length > 0 ||
      filt.items.length > 0 ||
      filt.quickFilterValues.length > 0,
    [stage, searchValue, selectedCommunities, filt]
  );

  // ── Persist helpers ───────────────────────────────────────────────────────

  const persistFilters = React.useCallback(
    (updatedItems: FilterItem[], updatedQuick: string[], updatedStage: string) => {
      setSoldFilters({ items: updatedItems, quickFilterValues: updatedQuick, stage: updatedStage });
    },
    []
  );

  const handlePaginationChange = (model: { page: number; pageSize: number }) => {
    setPaginationModel(model);
    setSoldPagination(model);
  };

  // ── Toolbar handlers ──────────────────────────────────────────────────────

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSearchValue(newValue);
    const newQuick = sanitizeQuickFilter([newValue]);
    setFilt((prev) => {
      const updated: Filters = { ...prev, quickFilterValues: newQuick };
      persistFilters(updated.items, newQuick, stage);
      return updated;
    });
  };

  const handleStageChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    setStage(newValue);
    setFilt((prev) => {
      const newItems = sanitizeFilterItems(rebuildItems(prev.items, "stageCode", newValue));
      persistFilters(newItems, sanitizeQuickFilter(prev.quickFilterValues), newValue);
      return { ...prev, items: newItems };
    });
  };

  const handleCommunityChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    setSelectedCommunities(value);
    setFilt((prev) => {
      const newItems = sanitizeFilterItems(rebuildItems(prev.items, "subdivision", value));
      const safeQuick = sanitizeQuickFilter(prev.quickFilterValues);
      persistFilters(newItems, safeQuick, stage);
      return { ...prev, items: newItems, quickFilterValues: safeQuick };
    });
  };

  const handleCommunityDelete = (val: string) => {
    const next = selectedCommunities.filter((c) => c !== val);
    setSelectedCommunities(next);
    setFilt((prev) => {
      const newItems = sanitizeFilterItems(rebuildItems(prev.items, "subdivision", next));
      const safeQuick = sanitizeQuickFilter(prev.quickFilterValues);
      persistFilters(newItems, safeQuick, stage);
      return { ...prev, items: newItems, quickFilterValues: safeQuick };
    });
  };

  const handleFilterModelChange = (newFilterModel: Filters) => {
    const safeItems = sanitizeFilterItems(newFilterModel.items ?? []);
    const safeQuick = sanitizeQuickFilter(newFilterModel.quickFilterValues ?? []);
    const safe: Filters = { ...newFilterModel, items: safeItems, quickFilterValues: safeQuick };
    setFilt(safe);
    persistFilters(safeItems, safeQuick, stage);
  };

  const handleClearFilters = () => {
    setStage(DEFAULT_STAGE);
    setSearchValue(DEFAULT_SEARCH);
    setSelectedCommunities(DEFAULT_COMMUNITIES);
    setCommunitySearch("");
    setFilt(EMPTY_FILTERS);
    persistFilters([], [], DEFAULT_STAGE);
  };

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadJobs = () => {
    setLoading(true);
    httpClient
      .get(`/api/jobs/`)
      .then((res) => {
        const uniqueIds: Record<number, boolean> = {};
        const dataArray = Array.isArray(res?.data) ? res.data : [];
        const uniqueObjects = dataArray.filter((obj: any) => {
          if (!uniqueIds[obj.jobId]) {
            uniqueIds[obj.jobId] = true;
            return true;
          }
          return false;
        });
        setRows(uniqueObjects as any);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
      });
  };

  React.useEffect(() => {
    loadJobs();
  }, []);

  // ── Columns (unchanged) ───────────────────────────────────────────────────

  const columns: GridColDef[] = [
    {
      field: "jobAddress",
      headerName: "Address",
      minWidth: 240,
      flex: 2,
      filterable: true,
      sortComparator: (v1: string, v2: string) => {
        const parse = (addr: string) => {
          const m = addr?.match(/^(\d+)\s+(.*)/);
          return m ? [m[2].toLowerCase(), Number(m[1])] : [addr?.toLowerCase() ?? "", 0];
        };
        const [name1, num1] = parse(v1);
        const [name2, num2] = parse(v2);
        if (name1 < name2) return -1;
        if (name1 > name2) return 1;
        return (num1 as number) - (num2 as number);
      },
      renderCell: (params: any) => {
        const isCreateCase =
          params.row.profitAnalysisId === 0 &&
          params.row.role === "Write" &&
          (params?.row?.closeDate ?? null) === null;
        const isViewCase =
          (params.row.profitAnalysisId !== 0 && params.row.role === "Read") ||
          (params.row.profitAnalysisId !== 0 &&
            params.row.role === "Write" &&
            (params?.row?.closeDate ?? null) !== null &&
            new Date(params.row.closeDate) >
            new Date(new Date().setDate(new Date().getDate() - 60)));
        const isReviseCase =
          params.row.profitAnalysisId !== 0 &&
          (params?.row?.closeDate ?? null) === null &&
          params.row.role === "Write";

        const href = isReviseCase
          ? `/sold/edit/${params.row.profitAnalysisId}`
          : isViewCase
            ? `/sold/view/${params.row.profitAnalysisId}`
            : "#";

        return (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: "100%", overflow: "hidden" }}>
            <Box sx={{ flexShrink: 0 }}>
              {isViewCase && (
                <Tooltip title={`View PA for ${params.row.jobAddress}`} placement="left">
                  <IconButton size="small" href={`/sold/view/${params.row.profitAnalysisId}`}>
                    <VisibilityIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              )}
              {isReviseCase && (
                <Tooltip title={`Revise PA for ${params.row.jobAddress}`} placement="left">
                  <IconButton size="small" href={`/sold/edit/${params.row.profitAnalysisId}`}>
                    <EditIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              )}
              {isCreateCase && (
                <Tooltip title={`Create PA for ${params.row.jobAddress}`} placement="left">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => navigation("/sold/create", { state: { ...params.row, method: "fromHist" } })}
                  >
                    <AddCircleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!isViewCase && !isReviseCase && !isCreateCase && <Box sx={{ width: 34 }} />}
            </Box>
            <Tooltip title={params.value ?? ""} placement="top">
              <Link
                color="inherit"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: (isCreateCase || isReviseCase || isViewCase) ? "pointer" : "default",
                  textDecoration: (isCreateCase || isReviseCase || isViewCase) ? "underline" : "none",
                }}
                href={href}
                onClick={(e) => {
                  if (isCreateCase) {
                    e.preventDefault();
                    navigation("/sold/create", { state: { ...params.row, method: "fromHist" } });
                  } else if (!isViewCase && !isReviseCase) {
                    e.preventDefault();
                  }
                }}
              >
                {params.value}
              </Link>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      field: "subdivision",
      headerName: "Subdivision",
      minWidth: 160,
      flex: 1.5,
      filterable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ""} placement="top">
          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}>
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "jobNumber",
      headerName: "Job #",
      headerAlign: "left",
      align: "left",
      minWidth: 120,
      flex: 0.8,
      filterable: true,
      valueGetter: (_params: any, row: any) => {
        if (!row?.projectNumber || !row?.jobNumber) return null;
        return `${row?.projectNumber}-${row?.jobNumber}`;
      },
      renderCell: (params) => <div>{params.value}</div>,
    },
    {
      field: "buyerCombined",
      headerName: "Buyer",
      minWidth: 200,
      flex: 1.5,
      filterable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ""} placement="top">
          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}>
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "stageCode",
      headerName: "Stage",
      filterable: true,
      minWidth: 100,
      flex: 0.7,
      align: "center",
      headerAlign: "center",
      valueGetter: (_params: any, row: any) => {
        const code = row?.stageCode ? Number(row.stageCode) : 0;
        const days =
          row?.stageDate && row?.stageCode === "10"
            ? Math.floor((Date.now() - new Date(row.stageDate).getTime()) / 86_400_000)
            : 0;
        // Pack into a single number: stage * 100000 + days
        // This sorts by stage first, then by days within stage 10
        return code * 100_000 + days;
      },
      renderHeader: () => (
        <Tooltip title="Stage Code [Days since entering Stage 10]" placement="top">
          <Box sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: "0.875rem", color: "#374151", cursor: "default", userSelect: "none", whiteSpace: "nowrap" }}>
            S&nbsp;[# days]
          </Box>
        </Tooltip>
      ),
      renderCell: (params) => {
        const code = params.row.stageCode ?? "";
        const stageDate = params.row.stageDate;
        const daysSince =
          stageDate
            ? Math.floor((Date.now() - new Date(stageDate).getTime()) / 86_400_000)
            : null;
        return (
          <Tooltip
            title={
              daysSince !== null && daysSince > 0 && code === "10"
                ? `Stage ${code} — ${daysSince} day${daysSince !== 1 ? "s" : ""} since entering Stage 10`
                : `Stage ${code}`
            }
            placement="top"
          >
            <Box component="span" sx={{ whiteSpace: "nowrap" }}>
              {code}
              {daysSince !== null && code === "10" && (
                <Box component="span" sx={{ color: "text.secondary", ml: 0.5, fontSize: "0.75rem" }}>
                  [{daysSince}d]
                </Box>
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "profitMargin",
      headerName: "Profit Proj. %",
      filterable: true,
      minWidth: 110,
      flex: 0.7,
      align: "center",
      headerAlign: "center",
      valueGetter: (_params: any, row: any) => (row?.profitMargin ?? null),
      valueFormatter: (value: any) => (value != null ? `${value}%` : ""),
    },
    {
      field: "paModifiedOn",
      headerName: "PA Updated On",
      minWidth: 120,
      flex: 1,
      filterable: true,
      align: "center",
      headerAlign: "center",
      valueGetter: (_params: any, row: any) => {
        const v =
          row.paModifiedOn && row.paModifiedOn !== "0001-01-01T00:00:00"
            ? row.paModifiedOn
            : row.profitAnalysisCreatedOn;
        if (!v || v === "0001-01-01T00:00:00" || row.profitAnalysisId === 0) return null;
        return new Date(v).getTime();
      },
      renderCell: (params) => (
        <div>{params.value ? moment(params.value).format("L") : ""}</div>
      ),
    },
    {
      field: "projectedCloseDate",
      headerName: "Proj. Closed Date",
      minWidth: 140,
      flex: 1,
      filterable: true,
      align: "center",
      headerAlign: "center",
      valueFormatter: (value: any) => {
        if (!value || value === "0001-01-01T00:00:00") return "";
        return moment(value).format("L");
      },
      renderCell: (params) => (
        <div>
          {params.row.profitAnalysisId === 0 || !params.row.projectedCloseDate
            ? ""
            : moment(params.row.projectedCloseDate).format("L")}
        </div>
      ),
    },
    {
      field: "closeDate",
      headerName: "Closed Date",
      minWidth: 120,
      flex: 1,
      filterable: true,
      align: "center",
      renderCell: (params) => (
        <div>{params.row.closeDate ? moment(params.row.closeDate).format("L") : ""}</div>
      ),
    },
  ];

  // ── Filtered rows + filter summary ────────────────────────────────────────

  const filteredRows = React.useMemo(
    () => applyFilters<RowType>(rows, filt, columns),
    [rows, filt]
  );

  const filterSummaryLines = React.useMemo(
    () => buildFilterSummary("Sold", stage, searchValue, selectedCommunities, filt, rows.length, filteredRows.length),
    [stage, searchValue, selectedCommunities, filt, rows.length, filteredRows.length]
  );

  const [printPortalEl, setPrintPortalEl] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    const el = document.createElement("div");
    el.id = SOLD_PRINT_PORTAL_ID;
    document.body.appendChild(el);
    setPrintPortalEl(el);
    return () => { el.remove(); };
  }, []);



  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Container maxWidth={false} disableGutters>
      <Card sx={{ mt: { xs: 2, sm: 4 }, border: "1px solid #e5e7eb", borderRadius: 2 }}>
        <CardContent sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>

          {/* ── Toolbar ─────────────────────────────────────────────────── */}
          <ListPageToolbar<RowType>
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
            selectedCommunities={selectedCommunities}
            onCommunityChange={handleCommunityChange}
            onCommunityDelete={handleCommunityDelete}
            communitySearch={communitySearch}
            onCommunitySearchChange={setCommunitySearch}
            onCommunityClose={() => setCommunitySearch("")}
            filteredSubdivisions={filteredSubdivisions}
            stage={stage}
            onStageChange={handleStageChange}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={handleClearFilters}
            paType="Sold"
            createHref="/sold/create"
            filteredRows={filteredRows}
            printColumns={PRINT_COLUMNS}
            filterSummaryLines={filterSummaryLines}
            legendItems={LEGEND_ITEMS}
          />

          {/* ── Print-only styles ── */}
          <style>{`
            @media print {
              @page { size: landscape; margin: 1cm; }
              body > *:not(#${SOLD_PRINT_PORTAL_ID}) { display: none !important; }
              #${SOLD_PRINT_PORTAL_ID} { display: block !important; }
            }
            #${SOLD_PRINT_PORTAL_ID} { display: none; }
          `}</style>

          {/* ── Mobile cards / Desktop DataGrid ── */}
          {isMobile ? (
            <Box sx={{ overflowY: "auto", maxHeight: "calc(100vh - 240px)" }}>
              {loading ? (
                <Typography variant="body2" sx={{ color: "#6b7280", textAlign: "center", py: 4 }}>Loading…</Typography>
              ) : filteredRows.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Typography variant="body2" sx={{ color: "#9ca3af" }}>No rows to display</Typography>
                </Box>
              ) : (
                filteredRows.map((row) => (
                  <SoldMobileCard
                    key={row.jobId}
                    row={row}
                    onNavigate={(path, state) => navigation(path, { state })}
                  />
                ))
              )}
            </Box>
          ) : (
            <Box sx={{ height: "calc(100vh - 240px)", width: "100%" }}>
              <DataGrid
                disableColumnSelector
                disableDensitySelector
                hideFooter
                getRowId={(row) => row.jobId}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationChange}
                filterMode="server"
                onFilterModelChange={(newFilterModel) =>
                  handleFilterModelChange(newFilterModel as Filters)
                }
                disableRowSelectionOnClick
                columns={columns}
                rows={filteredRows}
                rowCount={filteredRows.length}
                slots={{ noRowsOverlay: NoRows }}
                sx={{
                  height: "100%",
                  "&": {
                    border: "none",
                    "& .MuiDataGrid-columnHeaders": {
                      borderBottom: "1px solid #e5e7eb",
                      "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: 600,
                        textTransform: "uppercase",
                        fontSize: "0.875rem",
                        color: "#374151",
                      },
                    },
                    "& .MuiDataGrid-row": {
                      backgroundColor: "white",
                      borderBottom: "1px solid #e5e7eb",
                      "&:hover": { backgroundColor: "#f9fafb" },
                      "&:last-child": { borderBottom: "none" },
                    },
                    "& .MuiDataGrid-cell": {
                      border: "none",
                      padding: "1rem",
                      whiteSpace: "nowrap",
                      color: "#374151",
                    },
                    "& .MuiDataGrid-treeDataGroupingCellToggle": { alignSelf: "auto" },
                    "& .MuiDataGrid-iconSeparator": { display: "none" },
                    "& .MuiDataGrid-virtualScroller": { backgroundColor: "white" },
                    "& .MuiTablePagination-root": { color: "#6b7280" },
                    "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                      outline: "none",
                    },
                  },
                }}
                loading={loading}
              />
            </Box>
          )}
        </CardContent>
        {/* ── Hidden print table rendered into a body-level portal ── */}
        {printPortalEl && createPortal(
          <div style={{ padding: 16, fontFamily: "sans-serif" }}>
            {filterSummaryLines.map((line, i) => (
              <div
                key={i}
                style={{
                  fontSize: i === 0 ? 15 : 12,
                  fontWeight: i === 0 ? 700 : 400,
                  color: i === 0 ? "#111827" : "#6b7280",
                  marginBottom: i === 0 ? 4 : 2,
                }}
              >
                {line}
              </div>
            ))}
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11, marginTop: 12 }}>
              <thead>
                <tr>
                  {PRINT_COLUMNS.map((c) => (
                    <th
                      key={c.field}
                      style={{
                        border: "1px solid #d1d5db",
                        padding: "4px 8px",
                        backgroundColor: "#f3f4f6",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        textAlign: "left",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, i) => (
                  <tr key={row.jobId} style={{ backgroundColor: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                    {PRINT_COLUMNS.map((c) => (
                      <td
                        key={c.field}
                        style={{
                          border: "1px solid #e5e7eb",
                          padding: "3px 8px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.getValue(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
          printPortalEl
        )}

      </Card>
    </Container>
  );
}
